import { ComposedModal, ModalHeader } from '@carbon/react';
import type { AreaGeometry, GeometryUpload } from 'types/area-geometry';
import type { AreaWorkspace } from 'types/area-workspace';
import { EditorBody } from './editor-body';
import type { SavedDraftGeometry } from './types';
import { areaTypeLabel } from './utils';
import styles from './area-geometry-editor.module.scss';

interface AreaGeometryEditorProps {
  open: boolean;
  area: AreaGeometry;
  workspace: AreaWorkspace;
  /** The upload method's files, sent and checked by the caller's hook. */
  upload: GeometryUpload;
  /** Discards the working copy: Cancel, the back arrow and the X. */
  onClose: () => void;
  /** Fires after a save lands, with the saved shape and its Gross Acres. */
  onSaved: (saved: SavedDraftGeometry) => void;
}

/**
 * Edit Area Geometry (Story 5): the workspace's area on a map with drawing tools,
 * where survey rows or an uploaded file can add parts, saved as the workspace's draft
 * boundary. The body mounts per open, so each open starts from the area as it is
 * and a close discards without asking, as the mock does.
 *
 * @param {AreaGeometryEditorProps} props - The area, its workspace and the exits.
 * @return {JSX.Element} The editor modal.
 */
export function AreaGeometryEditor({
  open,
  area,
  workspace,
  upload,
  onClose,
  onSaved,
}: AreaGeometryEditorProps): JSX.Element {
  return (
    <ComposedModal
      open={open}
      // A close discards the working copy, so Esc never closes the editor: it
      // only cancels a shape in progress, and Cancel or the X close it.
      onClose={(event: { type?: string } | undefined) => {
        if (event?.type === 'keydown') return false;
        onClose();
        return true;
      }}
      size="lg"
      preventCloseOnClickOutside
      className={styles.modal}
      containerClassName={styles.modalContainer}
      selectorPrimaryFocus="#area-geometry-edit-map"
    >
      <ModalHeader
        label={`${area.displayName || area.name} · ${areaTypeLabel(area.category)}`}
        title="Edit Area Geometry"
        iconDescription="Close editor"
      />
      {open && (
        <EditorBody
          area={area}
          workspace={workspace}
          upload={upload}
          onClose={onClose}
          onSaved={onSaved}
        />
      )}
    </ComposedModal>
  );
}
